import { useDownload } from '@modules/file-menagement';
import { Button, IconButton, Typography, styled } from '@mui/material';
import cn from 'classnames';
import { useSnackbar } from 'notistack';
import { MouseEvent } from 'react';
import { CircularProgressWithLabel } from 'src/components/CircularProgressWithLabel';
import FileThumbnail from 'src/components/file-thumbnail';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import {
  isImageMimeType,
  isVideoMimeType,
} from 'src/sections/@dashboard/chat/ChatConversation/shared/utils';
import { fData } from 'src/utils/formatNumber';
import { FormatTime } from 'src/utils/formatTime';
import { useConversationMediaContext } from '../../conversation/providers/ConversationMedia';
import { useMessageMediaContent } from '../../conversation/state/ConversationMedia';
import { IConversationMessageContentProps } from './types';

export function ConversationMessageContentFile({
  message,
}: IConversationMessageContentProps) {
  const { mediaContent, getMediaContent } = useMessageMediaContent(message);
  const { setOpenMessageMedia } = useConversationMediaContext();
  const { downloadContent } = useDownload();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();

  const isPdf = message.mimeType === 'application/pdf';

  const onDownloadFile = async (e: MouseEvent) => {
    e.stopPropagation();
    if (!message.mediaRef) {
      enqueueSnackbar(
        translate('shared.erros.error_whe_trying_to_download', {
          message: message._id,
        }),
        { variant: 'error' },
      );
      return;
    }
    const mediaFile = await getMediaContent(message);
    downloadContent({ fileName: message.fileName as string, blob: mediaFile });
  };

  const handleOpenMessageMedia = async () => {
    if (!message.mediaRef) return;
    await getMediaContent(message);
    setOpenMessageMedia(message);
  };

  return (
    <ContentFileContainer
      className={cn({ active: isPdf })}
      onClick={() => isPdf && handleOpenMessageMedia()}
    >
      {isVideoMimeType(message.mimeType || '') ? (
        <Button
          color="secondary"
          variant="rounded"
          onClick={handleOpenMessageMedia}
        >
          <Iconify icon="material-symbols:play-arrow-rounded" />
        </Button>
      ) : isImageMimeType(message.mimeType || '') ? (
        <Button
          variant="text"
          color="secondary"
          className="image-button"
          onClick={handleOpenMessageMedia}
        >
          <FileThumbnail file={message.fileName || ''} />
        </Button>
      ) : (
        <FileThumbnail file={message.fileName || ''} />
      )}

      <div className="name-container">
        <Typography variant="subtitle2" noWrap color="text.primary">
          {message.fileName ||
            translate('shared.erros.error_getting_file_name')}
        </Typography>

        <div className="file-info">
          <Typography variant="caption">
            {fData(message.fileSize || 0)}
          </Typography>
          <span>•</span>
          <Typography variant="caption">
            {FormatTime.DateTime(message.createdAt)}
          </Typography>
        </div>
      </div>

      <div className="download-box">
        {mediaContent?.percentageLoaded ? (
          <CircularProgressWithLabel
            value={mediaContent?.percentageLoaded || 1}
          />
        ) : (
          <IconButton color="inherit" onClick={onDownloadFile}>
            <Iconify icon="tabler:download" />
          </IconButton>
        )}
      </div>
    </ContentFileContainer>
  );
}

const ContentFileContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  border: `solid 1px ${theme.palette.divider}`,
  maxWidth: 350,
  padding: theme.spacing(1.5),
  gap: theme.spacing(1.5),
  position: 'relative',

  '&.active': {
    cursor: 'pointer',
  },

  '& .name-container': {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    flex: 1,
  },
  '& .image-button': {
    minWidth: 'unset',
    padding: 0,
  },

  '& .file-info': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    lineHeight: 1,
    color: theme.palette.text.disabled,
  },

  '& .MuiButton-root': {
    flexShrink: 0,
  },

  '& .download-box': {
    position: 'absolute',
    top: 8,
    right: 8,
    flexShrink: 0,
    [theme.breakpoints.up('sm')]: {
      position: 'unset',
    },
  },
}));
